/**
 * VIVA HUB CRM — enter client intake manually on a lead
 */

import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import HubLayout from '../components/hub/HubLayout'
import CrmHelpLink from '../components/hub/crm/CrmHelpLink'
import CrmManualIntakeForm, {
  buildManualIntakePayload,
  emptyIntakeMinor,
  emptyIntakePerson,
} from '../components/hub/crm/CrmManualIntakeForm'
import { buildManualIntakeInitialState } from '../lib/crm-intake-prefill'
import { Button } from '@/components/ui/button'
import { hubCrmAPI } from '../utils/hub/api'

export default function HubCRMLeadIntake() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [leadName, setLeadName] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [readOnly, setReadOnly] = useState(false)

  const [clientType, setClientType] = useState('adult')
  const [serviceType, setServiceType] = useState('individual_counseling')
  const [language, setLanguage] = useState('en')
  const [adult, setAdult] = useState(emptyIntakePerson)
  const [person1, setPerson1] = useState(emptyIntakePerson)
  const [person2, setPerson2] = useState(emptyIntakePerson)
  const [minor, setMinor] = useState(emptyIntakeMinor)

  const loadLead = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const result = await hubCrmAPI.getLead(id)
      const lead = result.lead
      if (!lead) {
        setError('Case not found')
        return
      }
      setLeadName(lead.display_name)
      setReadOnly(lead.status === 'in_simple_practice' || lead.status === 'archived')

      const initial = buildManualIntakeInitialState({
        lead,
        participants: result.participants || [],
        contact_submissions: result.contact_submissions || [],
        latest_intake: result.latest_intake,
      })
      setClientType(initial.clientType)
      setServiceType(initial.serviceType)
      setLanguage(initial.language)
      setAdult(initial.adult)
      setMinor(initial.minor)
      setPerson1(initial.person1)
      setPerson2(initial.person2)
    } catch (err) {
      setError(err.message || 'Failed to load case')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadLead()
  }, [loadLead])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      const payload = buildManualIntakePayload({
        clientType,
        serviceType,
        language,
        adult,
        minor,
        person1,
        person2,
      })
      await hubCrmAPI.submitManualIntake(id, payload)
      navigate(`/hub/crm/${id}`)
    } catch (err) {
      setError(err.message || 'Failed to save intake')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <HubLayout appName="CRM">
        <p className="text-gray-600">Loading...</p>
      </HubLayout>
    )
  }

  if (readOnly) {
    return (
      <HubLayout appName="CRM">
        <p className="text-gray-700">This case is read-only and cannot receive a new intake.</p>
        <Link to={`/hub/crm/${id}`} className="text-green-700 text-sm mt-2 inline-block hover:underline">
          ← Back to case
        </Link>
      </HubLayout>
    )
  }

  return (
    <HubLayout appName="CRM">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <Link to={`/hub/crm/${id}`} className="text-sm text-green-700 hover:underline">
          ← Back to {leadName || 'case'}
        </Link>
        <CrmHelpLink />
      </div>

      <div className="max-w-3xl">
        <h1 className="text-xl font-bold text-gray-900">Intake form</h1>
        <p className="mt-2 text-sm text-gray-600">
          Use when the client shared intake form information by phone or in person. Data is saved
          on this case, appears under Latest intake form for Simple Practice copy, and sets status
          to Intake received when appropriate. No confirmation email is sent to the client.
        </p>

        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="mt-6 bg-white rounded-lg border border-gray-200 p-6 space-y-6"
        >
          <CrmManualIntakeForm
            clientType={clientType}
            setClientType={setClientType}
            serviceType={serviceType}
            setServiceType={setServiceType}
            language={language}
            setLanguage={setLanguage}
            adult={adult}
            setAdult={setAdult}
            minor={minor}
            setMinor={setMinor}
            person1={person1}
            setPerson1={setPerson1}
            person2={person2}
            setPerson2={setPerson2}
          />

          <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-gray-100">
            <Link
              to={`/hub/crm/${id}`}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </Link>
            <Button type="submit" variant="success" size="sm" disabled={saving}>
              {saving ? 'Saving...' : 'Save intake form'}
            </Button>
          </div>
        </form>
      </div>
    </HubLayout>
  )
}
